import { type SynastryV2Response } from '@/api/schema/synastry'
import { Emoji } from '@/app/components/Emoji/Emoji'
import { Col, Row } from '@/app/components/Flex/Flex'
import { Layout } from '@/app/components/Layout/Layout'
import { Loader } from '@/app/components/Loader/Loader'
import { Motion } from '@/app/components/Motion/Motion'
import { UI } from '@/app/components/UI/UI'
import { useBack } from '@/app/hooks/useBack'
import { useNavigate } from '@/app/hooks/useNavigate'
import RemoveSvg from '@/app/icons/remove.svg?react'
import ShareSvg from '@/app/icons/share.svg?react'
import { Compare } from '@/app/pages/SynastryPage/SynastryV2/Compare/Compare'
import { SynastryMain } from '@/app/pages/SynastryPage/SynastryV2/Main/SynastryMain'
import { Questions } from '@/app/pages/SynastryPage/SynastryV2/Questions/Questions'
import { RemovePopup } from '@/app/pages/SynastryPage/SynastryV2/RemovePopup/RemovePopup'
import { Share } from '@/app/pages/SynastryPage/SynastryV2/Share/Share'
import { useSession } from '@/app/state/session'
import { api } from '@/app/utils/api'
import { metrikaGoal } from '@/app/utils/metrika'
import type { FunctionalComponent } from 'preact'
import { useLayoutEffect, useState } from 'preact/hooks'
import { useParams } from 'wouter-preact'

const synastryCache = new Map<string, SynastryV2Response>()

export const SynastryV2: FunctionalComponent = () => {
  const back = useBack('~/tools/synastry')
  const route = useNavigate()
  const synastriesCount = useSession(s => s.user?.synastriesCount)

  const params = useParams<{ id: string; type: string }>()
  const [data, setData] = useState<SynastryV2Response | null>(null)
  const [shareVisible, setShareVisible] = useState(false)
  const [removeVisible, setRemoveVisible] = useState(false)

  const getSynastry = async (id: string) => {
    return api.get<SynastryV2Response>(`/synastry2/${id}`)
      .then(r => r.data)
  }

  const fetchData = async () => {
    const synastryId = params.id
    const cached = synastryCache.get(synastryId)
    const canShowCached = cached && (!cached.locked || synastriesCount === 0)

    setData(canShowCached ? cached : null)

    if (synastriesCount === undefined) return

    if (canShowCached) return

    let data = cached ?? await getSynastry(synastryId)

    synastryCache.set(synastryId, data)
    setData(data)
  }

  useLayoutEffect(() => {
    fetchData()
  }, [params.id])

  return (
    <Layout>
      {shareVisible && data && (
        <Share
          data={data}
          onClose={() => {
            setShareVisible(false)
          }}
        />
      )}

      {removeVisible && data && (
        <RemovePopup
          id={data.id}
          onSuccess={() => {
            route('~/tools/synastry', { replace: true })
          }}
          onClose={() => {
            setRemoveVisible(false)
          }}
        />
      )}

      <Layout.Content
        onBack={back}
        right={
          <>
            <UI.Icon
              onClick={() => {
                if (!data) return
                metrikaGoal('synastry_share')
                setShareVisible(true)
              }}
            >
              <ShareSvg strokeWidth={1.5} />
            </UI.Icon>
            <UI.Icon
              onClick={() => {
                if (!data) return
                setRemoveVisible(true)
              }}
            >
              <RemoveSvg strokeWidth={1.5} />
            </UI.Icon>
          </>
        }
      >
        {!data
          ? (
            <Loader.Container>
              <Loader type={params.type === 'astro' ? 'astro-synastry' : 'matrix-synastry'} />
            </Loader.Container>
          )
          : (
            <Motion>
              <Layout.Header>
                <Col alignItems='center' gap={4}>
                  {data.persone1Name}
                  <Emoji type='synastry' size={20} />
                  {data.persone2Name}
                </Col>
              </Layout.Header>

              <Row gap={30} gapMobile={20}>
                <Row gap={15}>
                  <SynastryMain
                    data={data}
                  />

                  <Compare
                    data={data}
                  />
                </Row>

                <Questions
                  data={data}
                />
              </Row>
            </Motion>
          )}
      </Layout.Content>
    </Layout>
  )
}
